import { createRef, Fragment, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Button } from '@upmc/ims.component-library';

import { WrapImsPacket } from '../../../../store/utility';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import WithLoadingHandler from '../../../../hoc/withLoadingHandler/withLoadingHandler';
import Card from '../../MDC/Card/Card';
import TextField from '../../MDC/TextField/TextField';
import { DataTable } from '../../MDC/DataTable/DataTable';
import Chip from '../../Chips/Chip/Chip';
import Icon from '../../MDC/Icon/Icon';
import { MDCListDivider } from '../../MDC/List/List';
import { Tooltip } from '../../MDC/Tooltip/Tooltip';
import { Headline6, Subtitle2 } from '../../MDC/Typography/Typography';
import { personSearchPropTypes } from './_types';
import {
	ELEMENT_NAMES,
	CSS_CLASSES,
	HEADERS,
	TEXT_CLASSES,
} from './_constants';
import { formatForID } from '../../../../utils/strings/StringUtil';

import './PersonSearch.scss';
import useDebounce from '../../../../utils/hooks/useDebounce';

const PersonSearch = ({ withoutFormik = false, ...props }) => {
	const [sFirstName, setFirstName] = useState('');
	const [sLastName, setLastName] = useState('');
	const [sMiddleInitial, setMiddleInitial] = useState('');
	const [sNetworkID, setNetworkID] = useState('');
	const [sPersonSelected, setPersonSelected] = useState(
		props.value
			? {
					Value: props.value,
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
			  }
			: null
	);
	const [sHasSearched, setHasSearched] = useState(false);
	const [hasScrolledToDataTable, setHasScrolledToDataTable] = useState(false);
	const [hasFocused, setHasFocused] = useState(false);
	const [focusCount, setFocusCount] = useState(0);

	const debouncedFocusCount = useDebounce(focusCount, 1000);

	const refTextFieldFirstName = createRef();
	const refTextFieldLastName = createRef();
	const refTextFieldMiddleInitial = createRef();
	const refTextFieldNetworkID = createRef();

	const dataTableRef = useRef(null);
	useEffect(() => {
		if (
			dataTableRef &&
			dataTableRef.current &&
			!hasScrolledToDataTable &&
			!!props.dataTable.rows.length &&
			props.shouldAutoScroll
		) {
			dataTableRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
			setHasScrolledToDataTable(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.dataTable.rows]);

	useEffect(() => {
		if (hasFocused) {
			if (focusCount === 0) {
				if (props.onBlur) {
					props.onBlur();
				}
			}
			if (focusCount === 1) {
				if (props.onFocus) {
					props.onFocus();
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedFocusCount, hasFocused]);

	/**
	 * Raised search event handler. Constructs payload for manual event bubbling, clears existing selection.
	 * @returns void
	 */
	const onButtonClickedHandler = () => {
		setPersonSelected(null);
		setHasSearched(true);
		setHasScrolledToDataTable(false);
		props.onButtonClickedHandler &&
			props.onButtonClickedHandler(
				WrapImsPacket({
					Target: props.requestTarget,
					Input: {
						First_Name: sFirstName?.trim(),
						Last_Name: sLastName?.trim(),
						Middle_Initial: sMiddleInitial?.trim(),
						Network_ID: sNetworkID?.trim(),
					},
				})
			);
	};

	/**
	 * Custom text field 'onkeyup' event handler. Triggers search if 'Enter' key pressed and search enabled.
	 * @param {Event} e keyup event.
	 * @returns void
	 */
	const onKeyUp = e => {
		if (e.key === 'Enter' && canSearch) {
			onButtonClickedHandler();
		}
		e.preventDefault();
	};

	/**
	 * Manage the multiple text inputs' blur events to determine if focus has moved away from PersonSearch entirely
	 * @param {Event} e onBlur event
	 */
	const onBlurHandler = e => {
		setFocusCount(focusCount - 1);
	};

	/**
	 * Manage the multiple text inputs' focus events to determine if focus has moved away from PersonSearch entirely
	 * @param {Event} e onFocus event
	 */
	const onFocusHandler = e => {
		setFocusCount(focusCount + 1);
		setHasFocused(true);
	};

	/**
	 * Custom data table row 'click' event handler. Only applied when component not of type 'multiPerson'.
	 * Stores display cell of clicked row object in local state and constructs payload for manual event bubbling.
	 * Display cell defaults to 'Name' unless overwritten in 'props'.
	 * @param {Object} row clicked row object
	 * @returns void
	 */
	const onRowClickedHandler = row => {
		setPersonSelected(
			props.displayHeader &&
				props.displayHeader.Value &&
				Object.keys(row.Cells).includes(
					props.displayHeader.Value.replace(/ /g, '_')
				)
				? row.Cells[props.displayHeader.Value.replace(/ /g, '_')]
				: row.Cells.Name
		);
		setHasSearched(false);
		props.dataTable &&
			props.dataTable.onRowClickedHandler &&
			props.dataTable.onRowClickedHandler(WrapImsPacket(row));
	};

	let canSearch = sFirstName || sLastName || sMiddleInitial || sNetworkID;
	let inputs = (
		<Fragment>
			<TextField
				canClear
				help=' '
				inputProps={{
					id: `${ELEMENT_NAMES.INPUT}__${formatForID(
						TEXT_CLASSES.FIRST_NAME
					)}`,
					onKeyUp: e => onKeyUp(e),
				}}
				label={TEXT_CLASSES.FIRST_NAME}
				onChange={e => setFirstName(e.target.value)}
				onBlur={onBlurHandler}
				onFocus={onFocusHandler}
				ref={refTextFieldFirstName}
				trailingIcon={
					<Icon
						icon=''
						id={`${ELEMENT_NAMES.INPUT}__${formatForID(
							TEXT_CLASSES.FIRST_NAME
						)}__clear`}
					/>
				}
				data-test={ELEMENT_NAMES.INPUT}
				withoutFormik={withoutFormik}
			/>
			<TextField
				canClear
				help=' '
				inputProps={{
					id: `${ELEMENT_NAMES.INPUT}__${formatForID(
						TEXT_CLASSES.LAST_NAME
					)}`,
					onKeyUp: e => onKeyUp(e),
				}}
				label={TEXT_CLASSES.LAST_NAME}
				onChange={e => setLastName(e.target.value)}
				onBlur={onBlurHandler}
				onFocus={onFocusHandler}
				ref={refTextFieldLastName}
				trailingIcon={
					<Icon
						icon=''
						id={`${ELEMENT_NAMES.INPUT}__${formatForID(
							TEXT_CLASSES.LAST_NAME
						)}__clear`}
					/>
				}
				data-test={ELEMENT_NAMES.INPUT}
				withoutFormik={withoutFormik}
			/>
			<TextField
				canClear
				help=' '
				inputProps={{
					id: `${ELEMENT_NAMES.INPUT}__${formatForID(
						TEXT_CLASSES.MIDDLE_INITIAL
					)}`,
					onKeyUp: e => onKeyUp(e),
				}}
				label={TEXT_CLASSES.MIDDLE_INITIAL}
				onChange={e => setMiddleInitial(e.target.value)}
				onBlur={onBlurHandler}
				onFocus={onFocusHandler}
				ref={refTextFieldMiddleInitial}
				trailingIcon={
					<Icon
						icon=''
						id={`${ELEMENT_NAMES.INPUT}__${formatForID(
							TEXT_CLASSES.MIDDLE_INITIAL
						)}__clear`}
					/>
				}
				data-test={ELEMENT_NAMES.INPUT}
				withoutFormik={withoutFormik}
			/>
			<TextField
				canClear
				help=' '
				inputProps={{
					id: `${ELEMENT_NAMES.INPUT}__${formatForID(
						TEXT_CLASSES.NETWORK_ID
					)}`,
					onKeyUp: e => onKeyUp(e),
				}}
				label={TEXT_CLASSES.NETWORK_ID}
				onChange={e => setNetworkID(e.target.value)}
				onBlur={onBlurHandler}
				onFocus={onFocusHandler}
				ref={refTextFieldNetworkID}
				trailingIcon={
					<Icon
						icon=''
						id={`${ELEMENT_NAMES.INPUT}__${formatForID(
							TEXT_CLASSES.NETWORK_ID
						)}__clear`}
					/>
				}
				data-test={ELEMENT_NAMES.INPUT}
				withoutFormik={withoutFormik}
			/>
			<StyleAuxiliary classes={CSS_CLASSES.INPUT_OPTIONS}>
				{props.inputOptions}
			</StyleAuxiliary>
		</Fragment>
	);

	const areHeadersOverloaded =
		props.dataTable && Array.isArray(props.dataTable.headers);
	const headers = [
		...HEADERS.map(objHeader => {
			const overloadedIndex = areHeadersOverloaded
				? props.dataTable.headers.findIndex(
						header => header.Value === objHeader.Value
				  )
				: -1;
			objHeader.props =
				overloadedIndex > -1
					? props.dataTable.headers[overloadedIndex].props
					: null;
			return objHeader;
		}),
		...(areHeadersOverloaded
			? props.dataTable.headers.filter(
					header =>
						!HEADERS.find(
							baseHeader => baseHeader.Value === header.Value
						)
			  )
			: []),
	];

	let output = sHasSearched &&
		!sPersonSelected &&
		!props.errorMessage &&
		!props.isLoading && (
			<div ref={dataTableRef}>
				<DataTable
					{...props.dataTable}
					indexed={props.dataTable?.indexed ?? true}
					headers={headers}
					onRowClickedHandler={
						props.multiPerson
							? null
							: row => onRowClickedHandler(row)
					}
					stickyRows={1}
					data-test={ELEMENT_NAMES.DATA_TABLE}
					withoutFormik={withoutFormik}
				/>
			</div>
		);

	return (
		<Card
			className={classnames(props.className, CSS_CLASSES.CONTAINER, {
				[`${CSS_CLASSES.FIT_CONTAINER}`]: !output,
			})}
			xlarge
			data-test={props['data-test']}
		>
			<StyleAuxiliary classes={CSS_CLASSES.INPUT_CONTAINER}>
				<Headline6 data-test={ELEMENT_NAMES.LABEL}>
					{props.label}
				</Headline6>
				{inputs}
				<Button
					id={`${ELEMENT_NAMES.BUTTON}__search`}
					variant='outlined'
					onClick={() => onButtonClickedHandler()}
					disabled={!canSearch}
					data-test={ELEMENT_NAMES.BUTTON}
				>
					Search
				</Button>
				{props.errorMessage ? (
					<Subtitle2
						className={CSS_CLASSES.INVALID}
						data-test={ELEMENT_NAMES.MESSAGE_ERROR}
					>
						{props.errorMessage}
					</Subtitle2>
				) : props.warningMessage ? (
					<Subtitle2
						className={CSS_CLASSES.WARNING}
						data-test={ELEMENT_NAMES.MESSAGE_WARNING}
					>
						{props.warningMessage}
					</Subtitle2>
				) : null}
				{sPersonSelected &&
					sPersonSelected.Value &&
					!props?.options?.noChips && (
						<Fragment>
							<MDCListDivider
								className={CSS_CLASSES.INPUT_DIVIDER}
								tag='div'
							/>
							<Tooltip
								activateOn='hover'
								align='right'
								content={TEXT_CLASSES.SELECTED}
							>
								<Chip
									handleTrailingIconInteraction={() => {
										setPersonSelected(null);
										if (props.onChipRemovedHandler) {
											props.onChipRemovedHandler();
										}
									}}
									id={`${ELEMENT_NAMES.SELECTED_PERSON_CHIP}`}
									label={
										sPersonSelected.Value.length > 27
											? sPersonSelected.Value.slice(
													0,
													27
											  ) + '...'
											: sPersonSelected.Value
									}
									leadingIcon={<Icon icon='person' />}
									trailingIcon={
										<Icon
											icon='cancel'
											id={`${ELEMENT_NAMES.SELECTED_PERSON_CHIP}__remove`}
										/>
									}
									data-test={
										ELEMENT_NAMES.SELECTED_PERSON_CHIP
									}
								/>
							</Tooltip>
						</Fragment>
					)}
			</StyleAuxiliary>
			<WithLoadingHandler
				isLoading={props.isLoading}
				data-test={ELEMENT_NAMES.LOADING}
			>
				{output}
			</WithLoadingHandler>
		</Card>
	);
};

PersonSearch.displayName = 'PersonSearch';
PersonSearch.propTypes = personSearchPropTypes;

export default PersonSearch;
